import React from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  TimePicker,
  Typography,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface PackageOption {
  label: string;
  value: string;
  price: number;
}

interface PackageGroup {
  label: string;
  options: PackageOption[];
}

interface SizeOption {
  value: string;
  label: string;
  price?: number;
  realPrice?: number;
}

interface Props {
  form: any;
  onFinish: () => void;
  onValuesChange: (changed: any, all: any) => void;
  currentRow: any;
  isSubmitDisabled: boolean;
  packageGroups?: PackageGroup[];
}

const DEFAULT_PACKAGE_OPTIONS: PackageOption[] = [
  { label: "60м Энгийн", value: "60м Энгийн", price: 60000 },
  { label: "30м Энгийн", value: "30м Энгийн", price: 40000 },
  { label: "60м Энгийн Жирэмсний", value: "60м Энгийн Жирэмсний", price: 80000 },
  { label: "60м Хүүхдийн багц", value: "60м Хүүхдийн багц", price: 100000 },
  { label: "60м Хөрөг", value: "60м Хөрөг", price: 100000 },
  { label: "30м Хөрөг", value: "30м Хөрөг", price: 50000 },
  { label: "60м Хөрөг Жирэмсний", value: "60м Хөрөг Жирэмсний", price: 100000 },
];

const SIZE_OPTIONS: SizeOption[] = [
  { value: "A6", label: "A6", price: 2500 },
  { value: "A5", label: "A5", price: 5000 },
  { value: "A4", label: "A4", price: 10000 },
  { value: "A3", label: "A3", price: 15000 },
];

const FRAME_OPTIONS: SizeOption[] = [
  { value: "A5", label: "A5", price: 10000 },
  { value: "A4", label: "A4", price: 15000 },
  { value: "A3", label: "A3", price: 30000 },
];

const FRAME_AND_PAPER_OPTIONS: SizeOption[] = [
  { value: "A5", label: "A5", price: 13000 },
  { value: "A4", label: "A4", price: 20000 },
  { value: "A3", label: "A3", price: 40000 },
];

const CANVAS_OPTIONS: SizeOption[] = [
  { value: "15x20", label: "15x20", price: 25000, realPrice: 10000 },
  { value: "20x30", label: "20x30", price: 35000, realPrice: 15000 },
  { value: "30x40", label: "30x40", price: 55000, realPrice: 25000 },
  { value: "40x60", label: "40x60", price: 85000, realPrice: 40000 },
  { value: "50x70", label: "50x70", price: 125000, realPrice: 55000 },
  { value: "60x70", label: "60x70", price: 145000, realPrice: 55000 },
  { value: "60x80", label: "60x80", price: 165000, realPrice: 55000 },
  { value: "60x90", label: "60x90", price: 185000, realPrice: 55000 },
  { value: "60x100", label: "60x100", price: 200000, realPrice: 55000 },
];

const normalizePackageOptions = (
  groups?: PackageGroup[],
  currentPackage?: string
) => {
  const optionMap = new Map<string, PackageOption>();
  const fetchedOptions = groups?.flatMap((group) => group.options || []) || [];
  const options =
    fetchedOptions.length > 0 ? fetchedOptions : DEFAULT_PACKAGE_OPTIONS;

  options.forEach((option) => {
    if (option?.value) {
      optionMap.set(option.value, option);
    }
  });

  if (currentPackage && !optionMap.has(currentPackage)) {
    optionMap.set(currentPackage, {
      label: currentPackage,
      value: currentPackage,
      price: 0,
    });
  }

  return Array.from(optionMap.values());
};

const EditableItemList: React.FC<{
  name: string;
  title: string;
  addLabel: string;
  options: SizeOption[];
  canvas?: boolean;
}> = ({ name, title, addLabel, options, canvas = false }) => {
  const form = Form.useFormInstance();

  const applyCanvasSize = (index: number, size: string) => {
    const selected = options.find((option) => option.value === size);
    if (!selected) return;

    form.setFieldValue([name, index, "price"], selected.price || 0);
    form.setFieldValue([name, index, "realPrice"], selected.realPrice || 0);
  };

  return (
    <Card size="small" title={title} className="edit-section-card">
      <Form.List name={name}>
        {(fields, { add, remove }) => (
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            {fields.map((field) => (
              <Row key={field.key} gutter={[8, 8]} align="middle">
                <Col xs={24} md={canvas ? 5 : 9}>
                  <Form.Item
                    {...field}
                    name={[field.name, "size"]}
                    label="Хэмжээ"
                    rules={[{ required: true, message: "Хэмжээ сонгоно уу" }]}
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={options}
                      onChange={(value) => canvas && applyCanvasSize(field.name, value)}
                    />
                  </Form.Item>
                </Col>

                {canvas ? (
                  <>
                    <Col xs={24} md={5}>
                      <Form.Item
                        {...field}
                        name={[field.name, "code"]}
                        label="Code"
                        rules={[{ required: true, message: "Code оруулна уу" }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={5}>
                      <Form.Item {...field} name={[field.name, "price"]} label="Үнэ">
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={5}>
                      <Form.Item
                        {...field}
                        name={[field.name, "realPrice"]}
                        label="Өртөг"
                      >
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </>
                ) : (
                  <Col xs={20} md={11}>
                    <Form.Item
                      {...field}
                      name={[field.name, "count"]}
                      label="Тоо"
                      rules={[{ required: true, message: "Тоо оруулна уу" }]}
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                )}

                <Col xs={4} md={canvas ? 4 : 4}>
                  <Button
                    danger
                    type="text"
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(field.name)}
                  />
                </Col>
              </Row>
            ))}

            <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({})}>
              {addLabel}
            </Button>
          </Space>
        )}
      </Form.List>
    </Card>
  );
};

const EditForm: React.FC<Props> = ({
  form,
  onFinish,
  onValuesChange,
  currentRow,
  isSubmitDisabled,
  packageGroups,
}) => {
  const packageOptions = React.useMemo(
    () => normalizePackageOptions(packageGroups, currentRow?.packageName),
    [packageGroups, currentRow?.packageName]
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onValuesChange={onValuesChange}
      className="edit-booking-form"
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Card size="small" title="Үндсэн мэдээлэл" className="edit-section-card">
          <Row gutter={[12, 8]}>
            <Col xs={24} md={8}>
              <Form.Item name="year" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="day" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="bookingDate"
                label={<Text strong>Өдөр</Text>}
                rules={[{ required: true, message: "Өдөр сонгоно уу" }]}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  placeholder="Өдөр сонгох"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="bookedTime" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="bookingTime"
                label={<Text strong>Цаг</Text>}
                rules={[{ required: true, message: "Цаг сонгоно уу" }]}
              >
                <TimePicker
                  format="HH:mm"
                  minuteStep={5}
                  placeholder="Цаг сонгох"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="packageName"
                label={<Text strong>Багц</Text>}
                rules={[{ required: true, message: "Багц сонгоно уу" }]}
              >
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder="Багц сонгох"
                  options={packageOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[12, 8]}>
            <Form.Item name="paymenType" hidden>
              <Input />
            </Form.Item>
            <Col xs={24} md={8}>
              <Form.Item name="giftPhoto" valuePropName="checked" label="Gift photo">
                <Checkbox>Авсан</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="pickedUpCanvas"
                valuePropName="checked"
                label="Canvas авсан эсэх"
              >
                <Checkbox>Авсан</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title="Төлбөр" className="edit-section-card">
          <Row gutter={[12, 8]}>
            <Col xs={24} md={6}>
              <Form.Item name={["paymentBreakdown", "cash"]} label="Бэлэн">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name={["paymentBreakdown", "card"]} label="Карт">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name={["paymentBreakdown", "account"]} label="Данс">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="prePay" label="Урьдчилгаа">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="postPay" label="Үлдэгдэл">
                <InputNumber disabled min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="addPayment" label="Нэмэлт төлбөр">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="minusPayment" label="Хямдрал">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <EditableItemList
          name="pictures"
          title="Gift photo"
          addLabel="Gift photo нэмэх"
          options={SIZE_OPTIONS}
        />
        <EditableItemList
          name="paper"
          title="Нэмэлт зураг"
          addLabel="Зураг нэмэх"
          options={SIZE_OPTIONS}
        />
        <EditableItemList
          name="frame"
          title="Жааз"
          addLabel="Жааз нэмэх"
          options={FRAME_OPTIONS}
        />
        <EditableItemList
          name="frameAndPaper"
          title="Жаазтай зураг"
          addLabel="Жаазтай зураг нэмэх"
          options={FRAME_AND_PAPER_OPTIONS}
        />
        <EditableItemList
          name="canvas"
          title="Canvas"
          addLabel="Canvas нэмэх"
          options={CANVAS_OPTIONS}
          canvas
        />

        <Card size="small" title="Тайлбар" className="edit-section-card">
          <Form.Item name="description" label="Тайлбар">
            <Input.TextArea rows={3} placeholder="Нэмэлт мэдээлэл" />
          </Form.Item>
        </Card>

        <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitDisabled}
            size="large"
          >
            Хадгалах
          </Button>
        </Form.Item>
      </Space>
    </Form>
  );
};

export default EditForm;
